
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'page' | 'section';
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  declare state: ErrorBoundaryState;
  declare props: Readonly<ErrorBoundaryProps>;
  declare setState: Component<ErrorBoundaryProps, ErrorBoundaryState>['setState'];

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[Aura Traveler] Component error caught:', error);
    console.error('[Aura Traveler] Component stack:', errorInfo.componentStack);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const level = this.props.level || 'section';

    if (level === 'page') {
      return (
        <div className="min-h-screen bg-charcoal flex items-center justify-center px-6">
          <div className="max-w-md w-full text-center">
            <div className="w-12 h-px bg-gold mx-auto mb-8" />
            <h1 className="font-display text-2xl uppercase tracking-[0.25em] text-ivory mb-3">
              An Unexpected Turn of Events
            </h1>
            <p className="font-serif text-sm italic text-ivory/60 leading-relaxed mb-10">
              We encountered an interruption while crafting your journey. Your data is secure—let's try again.
            </p>
            <button
              onClick={this.handleRetry}
              className="px-8 py-3 border border-gold text-gold font-sans text-[10px] uppercase tracking-[0.2em] hover:bg-gold hover:text-charcoal transition-all duration-300"
            >
              Try Again
            </button>
            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <div className="mt-8 p-4 bg-white/5 border border-white/10 text-left">
                <p className="font-sans text-[10px] uppercase tracking-widest text-gold/60 mb-2">Debug Info</p>
                <p className="font-mono text-[10px] text-ivory/70 break-all">{this.state.error.message}</p>
              </div>
            )}
            <div className="mt-12 flex items-center justify-center gap-3 text-ivory/60">
              <span className="font-display text-sm tracking-[0.25em] uppercase">Aura</span>
              <span className="font-serif text-[10px] italic tracking-[0.4em]">Traveler</span>
            </div>
          </div>
        </div>
      );
    }

    // Section-level fallback
    return (
      <div className="w-full py-12 px-6 flex flex-col items-center justify-center bg-sand/10 border border-sand">
        <div className="w-8 h-px bg-gold mx-auto mb-4" />
        <p className="font-serif text-sm italic text-charcoal/90 mb-1">
          This section could not be displayed
        </p>
        <p className="font-sans text-[10px] uppercase tracking-widest text-charcoal/80 mb-6">
          Something unexpected happened
        </p>
        <button
          onClick={this.handleRetry}
          className="px-6 py-2 border border-gold/50 text-gold font-sans text-[10px] uppercase tracking-[0.2em] hover:bg-gold hover:text-charcoal transition-all duration-300"
        >
          Try Again
        </button>
        {process.env.NODE_ENV !== 'production' && this.state.error && (
          <p className="mt-4 font-mono text-[10px] text-charcoal/80 break-all max-w-md text-center">
            {this.state.error.message}
          </p>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
